import { useMemo } from 'react';
import { Button, Card, Col, Flex, Progress, Row, Statistic, Typography } from 'antd';
import { Cube } from '@phosphor-icons/react';
import type { WmsShipmentInboundType } from 'schemas/shipment/inbound/detail';
import { COLOR } from '@styles/theme';
import { InboundIntakeStepPanel } from './InboundIntakeStepPanel';

const { Text } = Typography;

type Pallet = WmsShipmentInboundType['pallets'][number];

type InboundShipmentStorageAssignmentStepProps = {
  pallets: Pallet[];
  onMarkCompleted?: () => void;
  markingCompleted?: boolean;
};

export type StorageAssignmentStats = {
  withContentsCount: number;
  withoutContentsCount: number;
  leftToStoreCount: number;
  storedCount: number;
  allWithContentsStored: boolean;
  hasPalletsWithContents: boolean;
};

export function getStorageAssignmentStats(pallets: Pallet[]): StorageAssignmentStats {
  const withContents = pallets.filter(pallet => pallet.contents.length > 0);
  const withoutContents = pallets.filter(pallet => !pallet.contents.length);
  const leftToStore = withContents.filter(pallet => !pallet.embeddedIn);
  const storedCount = withContents.length - leftToStore.length;

  return {
    withContentsCount: withContents.length,
    withoutContentsCount: withoutContents.length,
    leftToStoreCount: leftToStore.length,
    storedCount,
    allWithContentsStored: withContents.length > 0 && leftToStore.length === 0,
    hasPalletsWithContents: withContents.length > 0,
  };
}

type StorageStatItemProps = {
  label: string;
  value: number;
  emphasis?: 'default' | 'pending' | 'complete';
};

function StorageStatItem({ label, value, emphasis = 'default' }: StorageStatItemProps) {
  const isPending = emphasis === 'pending' && value > 0;
  const isComplete = emphasis === 'complete' && value === 0;

  const valueColor = isPending ? COLOR.danger : isComplete ? COLOR.success : COLOR.black;

  const borderColor = isPending
    ? COLOR.danger
    : isComplete
      ? COLOR.success
      : COLOR.border;

  return (
    <Card
      size="small"
      style={{
        textAlign: 'center',
        background: COLOR.box,
        borderColor,
      }}
      styles={{ body: { padding: '16px 12px' } }}
    >
      <Statistic
        title={label}
        value={value}
        valueStyle={{ fontSize: 24, fontWeight: 700, color: valueColor }}
      />
    </Card>
  );
}

export function InboundShipmentStorageAssignmentStep({
  pallets,
  onMarkCompleted,
  markingCompleted = false,
}: InboundShipmentStorageAssignmentStepProps) {
  const stats = useMemo(() => getStorageAssignmentStats(pallets), [pallets]);

  const canMarkCompleted = stats.allWithContentsStored && Boolean(onMarkCompleted);
  const progressPercent = stats.withContentsCount
    ? Math.round((stats.storedCount / stats.withContentsCount) * 100)
    : 0;

  const title = stats.allWithContentsStored
    ? 'Ready to complete'
    : 'Storage assignment in progress';

  const description = stats.allWithContentsStored
    ? 'All pallets with contents have been put away. Mark this shipment complete when intake is finished.'
    : 'Counted items are being put away to storage locations. Line status will update to Stored as each product is assigned.';

  return (
    <InboundIntakeStepPanel
      icon={<Cube size={32} color={COLOR.accent} />}
      title={title}
      description={description}
      action={
        canMarkCompleted ? (
          <Button type="primary" loading={markingCompleted} onClick={onMarkCompleted}>
            Mark shipment as completed
          </Button>
        ) : undefined
      }
    >
      <Flex vertical align="center" gap={20} style={{ width: '100%' }}>
        {stats.hasPalletsWithContents ? (
          <Flex vertical gap={8} style={{ width: '100%', maxWidth: 480 }}>
            <Flex justify="space-between" align="center">
              <Text type="secondary" style={{ fontSize: 13 }}>
                Putaway progress
              </Text>
              <Text style={{ fontSize: 13, fontWeight: 600 }}>
                {stats.storedCount} of {stats.withContentsCount} pallets stored
              </Text>
            </Flex>
            <Progress
              percent={progressPercent}
              showInfo={false}
              strokeColor={stats.allWithContentsStored ? COLOR.success : COLOR.accent}
              trailColor={COLOR.border}
              size="small"
            />
          </Flex>
        ) : (
          <Text type="secondary" style={{ fontSize: 13, maxWidth: 420 }}>
            No pallets with contents yet. Counts from the mobile app will appear here once
            pallets are loaded for putaway.
          </Text>
        )}

        <Row gutter={12} style={{ width: '100%', maxWidth: 480 }}>
          <Col span={8}>
            <StorageStatItem label="With contents" value={stats.withContentsCount} />
          </Col>
          <Col span={8}>
            <StorageStatItem
              label="Without contents"
              value={stats.withoutContentsCount}
            />
          </Col>
          <Col span={8}>
            <StorageStatItem
              label="Left to store"
              value={stats.leftToStoreCount}
              emphasis={
                !stats.hasPalletsWithContents
                  ? 'default'
                  : stats.leftToStoreCount > 0
                    ? 'pending'
                    : 'complete'
              }
            />
          </Col>
        </Row>
      </Flex>
    </InboundIntakeStepPanel>
  );
}
